// Audio waveform DSP shared across the chat feature:
// - ChatInput uses it to (a) draw the live preview after pause and
//   (b) attach waveform_data to the outgoing message.
// - ChatMessage uses it to render the bubble's audio player bars.
// Keep the extraction identical in both places so the preview the sender
// sees on pause matches the waveform the recipient (and sender) sees once
// the message lands. See PR 1359 (web/audio-recorder) for context.

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export const extractWaveformData = (audioBuffer: AudioBuffer, samples = 100): number[] => {
  const channelData = audioBuffer.getChannelData(0)
  const blockSize = Math.max(1, Math.floor(channelData.length / samples))
  const waveform: number[] = []

  for (let i = 0; i < samples; i++) {
    const start = i * blockSize
    const end = Math.min(start + blockSize, channelData.length)
    let sum = 0
    for (let j = start; j < end; j++) {
      sum += Math.abs(channelData[j])
    }
    const avg = sum / Math.max(1, end - start)
    waveform.push(clamp(avg, 0, 1))
  }

  const maxValue = Math.max(...waveform, 0)
  if (maxValue <= 0) return waveform
  return waveform.map((value) => clamp(Math.pow(value / maxValue, 0.7), 0, 1))
}

export const resampleWaveform = (data: number[], targetSize: number): number[] => {
  if (!data.length) return Array.from({ length: targetSize }, () => 0)
  if (data.length === targetSize) return [...data]

  const step = data.length / targetSize
  return Array.from({ length: targetSize }, (_, index) => {
    const rawIndex = index * step
    const lowerIndex = Math.floor(rawIndex)
    const upperIndex = Math.min(data.length - 1, Math.ceil(rawIndex))
    const weight = rawIndex - lowerIndex
    const lowerValue = data[lowerIndex] ?? 0
    const upperValue = data[upperIndex] ?? lowerValue
    return lowerValue * (1 - weight) + upperValue * weight
  })
}

// Decode a recorded blob and produce a normalized 0..1 amplitude array.
// Returns null when running outside the browser, when AudioContext is
// unavailable, or when the blob can't be decoded. Each call owns its own
// AudioContext so it can't interfere with a long-lived shared one.
export const computeWaveformFromBlob = async (blob: Blob): Promise<number[] | null> => {
  if (!blob || blob.size === 0) return null
  if (typeof window === 'undefined' || typeof AudioContext === 'undefined') return null

  const audioContext = new AudioContext()
  try {
    if (audioContext.state === 'suspended') {
      await audioContext.resume()
    }
    const arrayBuffer = await blob.arrayBuffer()
    if (!arrayBuffer || arrayBuffer.byteLength === 0) return null
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer.slice(0))
    return extractWaveformData(audioBuffer)
  } catch (err) {
    console.warn('Failed to compute waveform from blob', err)
    return null
  } finally {
    await audioContext.close().catch(() => {})
  }
}
